// Hits to kill and one-hit odds on a hunt, from the same formula the hunt fights with (shared/combat.ts blowOdds).
// Shown on the creature page ("On this hunt"), in the calculator and in the Bestiary.
import { basicMove, blowOdds, kitMoves, moveCooldownMs, waveMove, type BlowOdds, type Fighter } from '../shared/combat.ts';
import { WAVE_SPLASH } from '../shared/moves.ts';
import { wildAt } from '../shared/analyser.ts';
import { SPECIES } from '../shared/species.ts';
import type { GroundDef } from '../shared/grounds.ts';
import { esc, elChip } from './fmt.ts';

const pct = (x: number) => (x <= 0 ? '0%' : x >= 1 ? '100%' : x < 0.01 ? '<1%' : x > 0.99 ? '>99%' : `${Math.round(x * 100)}%`);
const hitsText = (o: BlowOdds) => (o.hitsLo === o.hitsHi ? `${o.hitsLo}` : `${o.hitsLo}-${o.hitsHi}`);

/** A row of the table; splash: the Wave's blow on a wild next to its target (WAVE_SPLASH of the power). */
export type OddsRow = BlowOdds & { splash?: boolean };

/**
 * Every move of the lead's kit against an average wild of the hunt: damage, blows to kill and the one-hit chance.
 * The Wave (Lv 60) has two rows: on its target, and its splash on the wilds next to it.
 */
export function huntOdds(lead: Fighter, g: GroundDef): { wild: Fighter; rows: OddsRow[] } {
  const wild = wildAt(g);
  const rows: OddsRow[] = [basicMove(lead), ...kitMoves(lead)].map((m) => blowOdds(lead, wild, m, 'hunt', wild.stats.hp));
  const wave = waveMove(lead);
  if (wave) rows.push(blowOdds(lead, wild, wave, 'hunt', wild.stats.hp), { ...blowOdds(lead, wild, wave, 'hunt', wild.stats.hp, WAVE_SPLASH), splash: true });
  return { wild, rows };
}

/** A short line: blows to kill with the basic attack, and the best one-hit chance of any move. */
export function huntOddsLine(lead: Fighter, g: GroundDef): string {
  const { rows } = huntOdds(lead, g);
  const basic = rows[0];
  const best = rows.reduce((a, b) => (b.oneHit > a.oneHit ? b : a));
  const one = best.oneHit > 0 ? `${pct(best.oneHit)} with ${esc(best.move.name)}` : 'never';
  return `${hitsText(basic)} basic hits a wild · one hit: ${one}`;
}

/** The full table, with a header naming the wild. */
export function huntOddsTable(lead: Fighter, g: GroundDef): string {
  const { wild, rows } = huntOdds(lead, g);
  const ws = SPECIES[wild.speciesId];
  const edge = lead.level / Math.max(1, g.level);
  return `<p class="muted">Against an average ${esc(ws.name)} (Lv ${g.level}, ${wild.stats.hp.toLocaleString()} HP)${edge !== 1 ? `, level edge ×${edge * edge >= 100 ? Math.round(edge * edge).toLocaleString('en-US') : +(edge * edge).toFixed(2)}` : ''}</p>
    <table class="tbl odds"><tr><th>Move</th><th class="num">Power</th><th>Element</th><th class="num">Damage</th><th class="num">Hits to kill</th><th class="num">One hit</th></tr>
    ${rows.map((o) => `<tr${o.splash ? ' class="splash"' : ''}><td>${o.splash ? `<span class="muted">↳ splash, ${Math.round(WAVE_SPLASH * 100)}%</span>` : `${esc(o.move.name)}${o.move.slot === 'basic' ? ' <span class="muted">basic</span>' : ` <span class="muted">${(moveCooldownMs(lead, o.move) / 1000).toFixed(1)} s${o.move.slot === 'coverage' && lead.tablet ? ' · Rune Tablet' : ''}</span>`}`}</td><td class="num">${o.splash ? Math.round(o.move.power * WAVE_SPLASH) : o.move.power}</td><td>${elChip(o.move.element)}${o.mult !== 1 ? ` ×${+o.mult.toFixed(2)}` : ''}</td>
      <td class="num">${o.lo.toLocaleString()}-${o.hi.toLocaleString()}</td><td class="num">${hitsText(o)}</td><td class="num${o.oneHit > 0 ? ' gold-text' : ''}">${pct(o.oneHit)}</td></tr>`).join('')}</table>`;
}
